import React, { useState, useEffect, useMemo, useRef } from 'react';
import { selectionAPI, productsAPI } from '../api/client';

function CheckList({ options, selected, onChange, placeholder = 'Поиск…', maxHeight = 220 }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(x => x !== val));
    else onChange([...selected, val]);
  };

  return (
    <div className="cklist">
      <div className="cklist-search">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="cklist-icon">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref={inputRef}
          className="cklist-input"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="cklist-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }}>×</button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="cklist-tags">
          {selected.map(s => (
            <span key={s} className="cklist-tag">
              {s}<button type="button" className="cklist-tag-x" onClick={() => toggle(s)}>×</button>
            </span>
          ))}
        </div>
      )}

      <div className="cklist-list" style={{ maxHeight }}>
        {filtered.length === 0 && <div className="cklist-empty">Ничего не найдено</div>}
        {filtered.map(opt => (
          <label key={opt} className={`cklist-item${selected.includes(opt) ? ' cklist-item--checked' : ''}`}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="cklist-checkbox" />
            <span>{opt}</span>
          </label>
        ))}
      </div>

      {options.length > 0 && (
        <div className="cklist-footer">
          <button
            type="button"
            className="cklist-footer-btn"
            onClick={() => onChange([...new Set([...selected, ...filtered])])}
            disabled={filtered.length === 0}
          >
            Выбрать все {filtered.length < options.length ? `"${query.trim()}" ` : ''}({filtered.length})
          </button>
          {selected.length > 0 && (
            <button type="button" className="cklist-footer-btn cklist-footer-btn--clear" onClick={() => onChange([])}>
              Снять все ({selected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SizeButtons({ sizes, value, onChange }) {
  if (!sizes.length) return null;
  return (
    <div className="size-btns">
      {sizes.map(s => (
        <button
          key={s}
          type="button"
          className={`size-btn${value === s ? ' size-btn--active' : ''}`}
          onClick={() => onChange(value === s ? '' : s)}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

export default function SelectionTab() {
  const [meta, setMeta]       = useState(null);
  const [metaErr, setMetaErr] = useState(false);
  const [allSizes, setAllSizes] = useState([]);

  const [models,   setModels]   = useState([]);
  const [size,     setSize]     = useState('');
  const [size2,    setSize2]    = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [search,   setSearch]   = useState('');
  const [supplier, setSupplier] = useState('');

  const [tgChatId, setTgChatId] = useState('');
  const [tgCustom, setTgCustom] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  useEffect(() => {
    selectionAPI.getMeta()
      .then(r => setMeta(r.data))
      .catch(() => setMetaErr(true));

    productsAPI.getSizes()
      .then(r => setAllSizes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const availableModels = useMemo(() => {
    if (!meta) return [];
    const set = new Set();
    Object.values(meta.brands_models).forEach(ms => ms.forEach(m => set.add(m)));
    return [...set].sort();
  }, [meta]);

  const hasFilters = models.length || size || size2 || maxPrice || search || supplier;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tgChatId.trim()) { setError('Укажите получателя'); return; }
    if (!hasFilters)       { setError('Укажите хотя бы один фильтр'); return; }

    setLoading(true);
    setResult(null);
    setError('');

    try {
      const r = await selectionAPI.send({
        models:     models.length ? models : null,
        size:       size.trim()    || null,
        size2:      size2.trim()   || null,
        max_price:  maxPrice ? parseFloat(maxPrice) : null,
        search:     search.trim()  || null,
        supplier:   supplier       || null,
        tg_chat_id: tgChatId.trim(),
      });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setModels([]); setSize(''); setSize2('');
    setMaxPrice(''); setSearch(''); setSupplier('');
    setResult(null); setError('');
  };

  if (metaErr) return <div style={{ padding: 24, color: 'var(--lg-red)' }}>Ошибка загрузки данных</div>;
  if (!meta)   return <div style={{ padding: 24, color: 'var(--lg-text-3)' }}>Загрузка…</div>;

  return (
    <div className="sel-wrap">
      <h2 className="sel-title">Подборка</h2>
      <p className="sel-hint">Настройте фильтры — бот отправит фото всех подходящих товаров в Telegram.</p>

      <form className="sel-form" onSubmit={handleSubmit}>

        {/* ── Получатель ─────────────────────────────────────── */}
        <div className="sel-recipient">
          <span className="sel-recipient-label">Получатель</span>
          {!tgCustom ? (
            <div className="sel-recipient-row">
              <select
                value={tgChatId}
                onChange={e => setTgChatId(e.target.value)}
                className="sel-recipient-select"
              >
                <option value="">— выбрать пользователя —</option>
                {meta.tg_users.map(u => (
                  <option key={u.tg_id} value={u.tg_id}>
                    {u.name} ({u.tg_id})
                  </option>
                ))}
              </select>
              <button type="button" className="sel-link-btn" onClick={() => { setTgCustom(true); setTgChatId(''); }}>
                Ввести вручную
              </button>
            </div>
          ) : (
            <div className="sel-recipient-row">
              <input
                type="text"
                value={tgChatId}
                onChange={e => setTgChatId(e.target.value)}
                placeholder="TG chat_id или @username"
                className="sel-recipient-input"
              />
              <button type="button" className="sel-link-btn" onClick={() => { setTgCustom(false); setTgChatId(''); }}>
                Выбрать из списка
              </button>
            </div>
          )}
        </div>

        {/* ── Модели ─────────────────────────────────────────── */}
        <div className="sel-section">
          <div className="sel-section-label">Модель <span className="sel-count">({availableModels.length})</span></div>
          <CheckList
            options={availableModels}
            selected={models}
            onChange={setModels}
            placeholder="Поиск модели…"
            maxHeight={240}
          />
        </div>

        {/* ── Размеры ────────────────────────────────────────── */}
        {allSizes.length > 0 && (
          <div className="sel-section">
            <div className="sel-sizes-rows">
              <div className="sel-size-row">
                <span className="sel-size-label">Размер</span>
                <SizeButtons sizes={allSizes} value={size} onChange={setSize} />
              </div>
              <div className="sel-size-row">
                <span className="sel-size-label">Доп. размер</span>
                <SizeButtons sizes={allSizes} value={size2} onChange={setSize2} />
              </div>
            </div>
          </div>
        )}

        {/* ── Остальные фильтры ──────────────────────────────── */}
        <div className="sel-grid">
          <label className="sel-field">
            <span>Поставщик</span>
            <select value={supplier} onChange={e => setSupplier(e.target.value)}>
              <option value="">— любой —</option>
              {meta.suppliers.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="sel-field">
            <span>Поиск в названии / артикуле</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Gore-tex, ALL12345…"
            />
          </label>

          <label className="sel-field">
            <span>Макс. цена</span>
            <input
              type="number"
              value={maxPrice}
              onChange={e => setMaxPrice(e.target.value.replace(',', '.'))}
              placeholder="5000"
              min="0"
            />
          </label>
        </div>

        {/* ── Кнопки ─────────────────────────────────────────── */}
        <div className="sel-actions">
          <button type="submit" className="sel-btn sel-btn--primary" disabled={loading}>
            {loading ? 'Отправляю…' : '📤 Отправить подборку'}
          </button>
          <button type="button" className="sel-btn sel-btn--ghost" onClick={handleReset} disabled={loading}>
            Сбросить
          </button>
        </div>

        {error && <div className="sel-alert sel-alert--err">{error}</div>}
        {result && !error && (
          <div className="sel-alert sel-alert--ok">
            {result.total_found === 0
              ? 'Нет подходящих товаров'
              : `✅ Отправлено ${result.sent} фото из ${result.total_found} найденных`}
          </div>
        )}

      </form>
    </div>
  );
}
